import { apiClient, MyTabsResponse } from '../utils/apiClient.js';
import { db } from '../db/index.js';
import { getCurrentUserId } from './routineSync.js';
import { LIBRARY_CHANGED_EVENT } from './events.js';
import { applyServerDifficulty } from '../db/difficulty.js';
import { toDifficultyLevel } from '../utils/difficulty.js';

/** @deprecated alias of LIBRARY_CHANGED_EVENT (FRE-142) */
export const TAB_STATUS_CHANGED_EVENT = LIBRARY_CHANGED_EVENT;

/** `GET /api/tabs/mine` returns at most this many rows (`take: 500`). */
const MINE_PAGE_CAP = 500;

let inFlight: Promise<number> | null = null;
let warnedForeignOwner = false;

async function run(): Promise<number> {
  const userId = getCurrentUserId();
  if (!userId || !navigator.onLine) return 0;

  const startedAt = Date.now();
  try {
    const res = await apiClient.get<MyTabsResponse>('/api/tabs/mine');
    // A response without an array `data` changes nothing (e.g. the E2E catch-all mock returns [])
    if (!Array.isArray(res?.data)) return 0;

    let changed = 0;
    for (const item of res.data) {
      if (!item || typeof item.id !== 'string') continue;
      const local = await db.tabs.get(item.id);
      if (!local) {
        // FRE-142 D6: restore as a metadata-only row; the file is downloaded on first open.
        await db.tabs.put({
          id: item.id,
          title: item.title,
          artist: item.artist,
          tuning: item.tuning,
          fileUrl: item.fileUrl,
          status: item.status,
          rejectReason: item.rejectReason ?? null,
          createdAt: item.createdAt,
          uploaderId: userId,
          ownerId: userId,
          isDownloaded: false,
          seenInMine: 1,
          rhythmDifficulty: toDifficultyLevel(item.rhythmDifficulty),
          leadDifficulty: toDifficultyLevel(item.leadDifficulty)
        });
        changed += 1;
        continue;
      }
      if (local.removedAt) continue; // deleted on this device: do not bring it back
      if (local.ownerId !== userId) {
        // One local copy per id (FRE-141): another account's copy stays untouched.
        if (!warnedForeignOwner) {
          warnedForeignOwner = true;
          console.info('Own tab restore skipped: the local copy belongs to another account', item.id);
        }
        continue;
      }
      // FND-12: positively seen as this user's own server tab; only such rows may be pruned later.
      if (local.seenInMine !== 1) await db.tabs.update(item.id, { seenInMine: 1 });
      // FRE-188 TL-10: difficulty is pulled unless an owner edit is still queued
      if (await applyServerDifficulty(item.id, item)) changed += 1;
      const nextReason = item.rejectReason ?? null;
      if (
        local.status !== item.status ||
        (local.rejectReason ?? null) !== nextReason ||
        local.uploaderId !== userId
      ) {
        // Moderation fields only: never touch localBlob, isDownloaded, title, etc.
        await db.tabs.update(item.id, {
          status: item.status,
          rejectReason: nextReason,
          uploaderId: userId
        });
        changed += 1;
      }
    }

    // FND-12: /mine is capped at 500 rows, so only a short list proves a row is gone from the server.
    if (res.data.length < MINE_PAGE_CAP) {
      const serverIds = new Set<string>();
      for (const item of res.data) if (item && typeof item.id === 'string') serverIds.add(item.id);
      const locals = await db.tabs.where('ownerId').equals(userId).toArray();
      for (const row of locals) {
        if (serverIds.has(row.id)) continue;
        if (row.seenInMine !== 1) continue; // downloaded/foreign copies were never in /mine
        if (row.uploaderId && row.uploaderId !== userId) continue;
        if (row.removedAt || row.pendingUpload === 1 || row.pendingServerDelete === 1) continue;
        // Uploaded in the meantime (created after this refresh began): the snapshot is stale for it.
        if (row.createdAt && Date.parse(row.createdAt) >= startedAt) continue;
        // Re-read: an upload may have finished while the loop above awaited.
        const fresh = await db.tabs.get(row.id);
        if (!fresh || fresh.removedAt || fresh.pendingUpload === 1 || fresh.ownerId !== userId) continue;
        if (fresh.seenInMine !== 1) continue;
        if (fresh.localBlob) {
          if (fresh.removedFromServer !== 1) {
            await db.tabs.update(row.id, { removedFromServer: 1 });
            changed += 1;
          }
        } else {
          await db.tabs.delete(row.id);
          changed += 1;
        }
      }
    }

    if (changed > 0) window.dispatchEvent(new Event(LIBRARY_CHANGED_EVENT));
    return changed;
  } catch (err) {
    // 401/404/network: silent, never toast
    console.warn('Own tab status refresh failed:', err);
    return 0;
  }
}

/** Pulls GET /api/tabs/mine: updates moderation fields of local tabs and restores missing own tabs. Returns rows changed. */
export function refreshOwnTabStatuses(): Promise<number> {
  if (inFlight) return inFlight;
  inFlight = run().finally(() => {
    inFlight = null;
  });
  return inFlight;
}
